import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import mixpanel, { Dict } from "mixpanel-browser";
import {
	createContext,
	FC,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { sendCloudwatchMetric } from "services/external/cloudwatch";
import { setAnalyticsInstance } from "utils/analytics/analyticsUtils";
import { getFridgeId } from "utils/browser/localStorage";
import { log } from "utils/logger";
import { v4 as uuid, v5 as uuidv5 } from "uuid";

import { AnalyticsContextProps } from "types/mainTypes";
import { MixpanelTabletTypeEnum } from "types/mixpanelTypes";

import { shouldEnableFeature } from "../utils/shouldEnableFeature";

/**
 * Represents the context for analytics.
 **/
export const AnalyticsContext = createContext<AnalyticsContextProps>({
	track: () => null,
	reset: () => null,
	identify: () => null,
	people: {
		set: () => null,
	},
});

const CLOUDWATCH_UPTIME_CHECK_INTERVAL = 30000;
// Fixed namespace for UUID v5 generation of Mixpanel $insert_id (RFC 4122 app-specific namespace)
const MIXPANEL_DEDUP_NAMESPACE = "a11e4d6c-b8f0-4a3e-9c7d-2f5b8e1a3d09";

/**
 * AnalyticsContextProvider component
 */
export const AnalyticsContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const mixpanelInstance = mixpanel;
	const customerIoInstance = new AnalyticsBrowser();
	const fridgeId = getFridgeId();
	const initializedRef = useRef(false);
	const sessionIdRef = useRef<string>(uuid());
	const APP_VERSION = import.meta.env.APP_VERSION;
	const APP_BUILD = import.meta.env.VITE_BUILD_NUMBER;

	// Initialize Mixpanel
	mixpanelInstance.init(Config.tools.mixpanelProjectId, {
		api_host: "https://api-eu.mixpanel.com",
		property_blacklist: [
			"$city",
			"$region",
			"mp_country_code",
			"$browser",
			"$browser_version",
			"$device",
			"$current_url",
			"$initial_referrer",
			"$initial_referring_domain",
			"$os",
			"mp_lib",
			"$referrer",
			"$referring_domain",
			"$screen_height",
			"$screen_width",
			"$search_engine",
			"mp_keyword",
		],
	});

	// Initialize Customer.io
	if (Config.tools.customerIoWriteKey) {
		customerIoInstance
			.load({
				cdnURL: "https://cdp-eu.customer.io",
				writeKey: Config.tools.customerIoWriteKey,
			})
			.catch((e) => {
				log("Error while initializing Customer.io", "error", e);
			});
	}

	const track = useCallback(
		(name: string, props: Dict) => {
			const useAnalytics = shouldEnableFeature("useAnalytics");

			// Generate deterministic insert_id (valid UUID) for event deduplication
			// Uses UUID v5 to create a deterministic UUID from event name + identifier,
			// ensuring different event types for the same entity get distinct IDs
			const eventIdentifier = props.$order_id || props.$user_id || props.$dish_type_id || "";
			const dedupKey = eventIdentifier
				? `${name}:${eventIdentifier}`
				: `${name}:${sessionIdRef.current}`;
			const insertId = uuidv5(dedupKey, MIXPANEL_DEDUP_NAMESPACE);

			if (useAnalytics) {
				mixpanelInstance.track(name, {
					...props,
					$insert_id: insertId,
				});

				customerIoInstance
					.track(name, {
						...props,
						$source_of_stats: "Tablet",
						$tabletType: MixpanelTabletTypeEnum.Master,
						$fridge_id: fridgeId,
						$tablet_version: APP_VERSION,
						$tablet_build: APP_BUILD,
					})
					.catch(() => {
						log("Error while sending the track event to customer.io", "error");
					});
			} else {
				log("Not master tablet, not tracking", "info", false, name, {
					...props,
					insertId: insertId,
				});
			}
		},
		[fridgeId, APP_VERSION, APP_BUILD],
	);

	const reset = useCallback(() => {
		// Generate new session ID on reset
		const newSessionId = uuid();
		sessionIdRef.current = newSessionId;

		const commonProperties = {
			$session_id: newSessionId,
			$source_of_stats: "Tablet",
			$tabletType: MixpanelTabletTypeEnum.Master,
			$fridge_id: fridgeId,
			$tablet_version: APP_VERSION,
			$tablet_build: APP_BUILD,
		};

		// Reset Mixpanel
		mixpanelInstance.reset();
		mixpanelInstance.register(commonProperties);

		// Reset Customer.io
		if (Config.tools.customerIoWriteKey) {
			customerIoInstance.reset();
		}
	}, [fridgeId, APP_VERSION, APP_BUILD]);

	const identify = useCallback(
		(userId: string, userData?: { [key: string]: string | number | null }) => {
			// Identify user in Mixpanel
			mixpanelInstance.identify(userId);

			// Identify user in Customer.io with anonymous ID (sessionId)
			// This allows CustomerIO to associate previous anonymous events
			// from this session with the identified user
			const traits = {
				...userData,
				// Keep fridgeId as device_id for device tracking
				device_id: fridgeId,
			};
			customerIoInstance.identify(userId, traits);
		},
		[fridgeId],
	);

	const people = useMemo(
		() => ({
			set: (data: { [key: string]: string | number }) => {
				// Set user properties in Mixpanel
				mixpanelInstance.people.set(data);
			},
		}),
		[],
	);

	useEffect(() => {
		if (initializedRef.current) return; // guard clause
		initializedRef.current = true;

		const commonProperties = {
			$session_id: sessionIdRef.current,
			$source_of_stats: "Tablet",
			$tabletType: MixpanelTabletTypeEnum.Master,
			$fridge_id: fridgeId,
			$tablet_version: APP_VERSION,
			$tablet_build: APP_BUILD,
		};

		mixpanelInstance.register(commonProperties);

		if (Config.tools.customerIoWriteKey) {
			customerIoInstance.identify(undefined, {
				device_id: fridgeId,
			});
		}

		setAnalyticsInstance({
			track,
			reset,
			identify,
			people,
		});
	}, [track, reset, identify, people, fridgeId, APP_VERSION, APP_BUILD]);

	useEffect(() => {
		let interval = null;
		const shouldTrackToCloudwatch = shouldEnableFeature("useCloudwatch");
		interval = setInterval(() => {
			sendCloudwatchMetric(shouldTrackToCloudwatch);
		}, CLOUDWATCH_UPTIME_CHECK_INTERVAL);

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, []);

	const contextValue = useMemo(
		() => ({
			track,
			reset,
			identify,
			people,
		}),
		[track, reset, identify, people],
	);

	return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

/**
 * Retrieves the analytics context from the React Context API.
 *
 * @return {Object} The analytics context.
 */
export const useAnalyticsContext = () => {
	return useContext(AnalyticsContext);
};
